import { useState, useEffect } from 'react';
import { Target, Search, ChevronDown, ChevronUp, ExternalLink, Loader2 } from 'lucide-react';
import {
  InstrumentLayout,
  RunHistoryDropdown,
  InstrumentLoadingState,
  InstrumentErrorState,
  NoResultsYetState,
} from '@/components/growth-instruments';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useIdeas } from '@/hooks/useIdeas';
import { useInstrumentRuns, type InstrumentRun } from '@/hooks/useInstrumentRuns';
import { cn } from '@/lib/utils';
import {
  CompetitorSourceFinder,
  type CompetitorSearchInput,
  type CompetitorSearchOutput,
  type CompetitorResult,
  type ConfidenceLevel,
} from '@/services/growth-instruments';

// Group competitors by approach for display
interface CompetitorGroup {
  approach: string;
  description: string;
  competitors: CompetitorResult[];
}

function groupCompetitorsByApproach(competitors: CompetitorResult[]): CompetitorGroup[] {
  const groups: Record<string, CompetitorResult[]> = {
    direct: [],
    indirect: [],
    adjacent: [],
  };

  competitors.forEach(c => {
    groups[c.approach]?.push(c);
  });

  const groupConfig = {
    direct: { label: 'Direct Substitutes', description: 'Products solving the exact same problem for the same audience' },
    indirect: { label: 'Indirect Alternatives', description: 'Different solutions that address the same underlying need' },
    adjacent: { label: 'Adjacent Solutions', description: 'Products in a related space that could expand into yours' },
  };

  return Object.entries(groups)
    .filter(([_, competitors]) => competitors.length > 0)
    .map(([approach, competitors]) => ({
      approach: groupConfig[approach as keyof typeof groupConfig].label,
      description: groupConfig[approach as keyof typeof groupConfig].description,
      competitors,
    }));
}

function ConfidenceBadge({ confidence }: { confidence: ConfidenceLevel }) {
  const config = {
    low: { label: 'Low', className: 'bg-muted text-muted-foreground' },
    medium: { label: 'Med', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
    high: { label: 'High', className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
  };
  const { label, className } = config[confidence];
  return (
    <Badge variant="secondary" className={cn('text-xs font-normal', className)}>
      {label} match
    </Badge>
  );
}

function CompetitorCard({ competitor }: { competitor: CompetitorResult }) {
  const [sourcesOpen, setSourcesOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3 min-w-0">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <h4 className="font-medium text-foreground line-clamp-2 break-words min-w-0">{competitor.name}</h4>
        <ConfidenceBadge confidence={competitor.confidence} />
      </div>

      {/* Description */}
      <p className="text-sm text-foreground/80 break-words">{competitor.description}</p>

      {/* Target audience */}
      <div className="text-sm min-w-0">
        <span className="text-muted-foreground">For: </span>
        <span className="text-foreground/80 break-words">{competitor.targetAudience}</span>
      </div>

      {/* Overlap reasons */}
      <div className="space-y-1.5 min-w-0">
        <span className="text-xs text-muted-foreground">Why it matches:</span>
        <ul className="space-y-1">
          {competitor.overlapReasons.map((reason, idx) => (
            <li key={idx} className="text-sm text-foreground/80 flex items-start gap-2 min-w-0">
              <span className="text-muted-foreground mt-1.5 shrink-0">•</span>
              <span className="break-words">{reason}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Sources */}
      <Collapsible open={sourcesOpen} onOpenChange={setSourcesOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground gap-1">
            {sourcesOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {competitor.sources.length} source{competitor.sources.length !== 1 ? 's' : ''}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-1">
            {competitor.sources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-primary hover:underline break-all"
              >
                <ExternalLink className="h-3 w-3 shrink-0" />
                <span className="truncate">{source.title}</span>
              </a>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function CompetitorGroupSection({ group }: { group: CompetitorGroup }) {
  return (
    <div className="space-y-3 w-full min-w-0">
      <div className="space-y-1 min-w-0">
        <h3 className="text-sm font-medium text-foreground break-words">{group.approach}</h3>
        <p className="text-xs text-muted-foreground break-words">{group.description}</p>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {group.competitors.map((competitor) => (
          <CompetitorCard key={competitor.id} competitor={competitor} />
        ))}
      </div>
    </div>
  );
}

function InsightSummary({ insight }: { insight: CompetitorSearchOutput['insight'] }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4 w-full min-w-0">
      <h3 className="text-sm font-medium text-foreground">Analysis Summary</h3>
      
      <div className="space-y-3 text-sm min-w-0">
        <div className="space-y-1 min-w-0">
          <span className="text-muted-foreground">Where you overlap:</span>
          <p className="text-foreground/80 leading-relaxed break-words">{insight.whereYouOverlap}</p>
        </div>
        
        <div className="space-y-1 min-w-0">
          <span className="text-muted-foreground">Where you don't:</span>
          <p className="text-foreground/80 leading-relaxed break-words">{insight.whereYouDont}</p>
        </div>
        
        <div className="space-y-1 min-w-0">
          <span className="text-muted-foreground">What kind of competition this is:</span>
          <p className="text-foreground/80 leading-relaxed break-words">{insight.competitionType}</p>
        </div>
      </div>
    </div>
  );
}

export default function CompetitorAnalyzer() {
  const { ideas } = useIdeas();
  const focusedIdea = ideas.find(i => i.is_focus);
  const { runs, loading: runsLoading, saveRun } = useInstrumentRuns('competitor_analyzer');
  
  // Input state
  const [ideaDescription, setIdeaDescription] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [useMoment, setUseMoment] = useState('');
  const [region, setRegion] = useState('Bangladesh');
  
  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<CompetitorSearchOutput | null>(null);
  const [competitorGroups, setCompetitorGroups] = useState<CompetitorGroup[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Auto-fill from focused idea
  useEffect(() => {
    if (focusedIdea) {
      setIdeaDescription(focusedIdea.one_liner || focusedIdea.raw_input || '');
      setTargetCustomer(focusedIdea.audience || '');
      setUseMoment(focusedIdea.problem || '');
    }
  }, [focusedIdea]);

  const runAnalysis = async (inputs: CompetitorSearchInput) => {
    setAnalyzing(true);
    setError(null);
    try {
      const analysisResult = await CompetitorSourceFinder.findCompetitors(inputs);
      setResult(analysisResult);
      setCompetitorGroups(groupCompetitorsByApproach(analysisResult.competitors));
      
      // Save run to history
      const summaryText = analysisResult.insight.competitionType;
      await saveRun(
        inputs as unknown as Record<string, unknown>,
        analysisResult as unknown as Record<string, unknown>,
        summaryText,
        focusedIdea?.id
      );
    } catch (err) {
      console.error('Analysis failed:', err);
      setError('The analysis could not be completed. Please check your inputs and try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyze = () => {
    runAnalysis({
      ideaDescription,
      targetCustomer,
      useMoment,
      region,
    });
  };

  const handleRetry = () => {
    if (ideaDescription.trim()) {
      handleAnalyze();
    }
  };

  const handleLoadRun = (run: InstrumentRun) => {
    const inputs = run.inputs as unknown as CompetitorSearchInput;
    setIdeaDescription(inputs.ideaDescription || '');
    setTargetCustomer(inputs.targetCustomer || '');
    setUseMoment(inputs.useMoment || '');
    setRegion(inputs.region || 'Bangladesh');
    const loadedResult = run.results as unknown as CompetitorSearchOutput;
    setResult(loadedResult);
    setCompetitorGroups(groupCompetitorsByApproach(loadedResult.competitors));
  };

  const handleRerun = (run: InstrumentRun) => {
    const inputs = run.inputs as unknown as CompetitorSearchInput;
    setIdeaDescription(inputs.ideaDescription || '');
    setTargetCustomer(inputs.targetCustomer || '');
    setUseMoment(inputs.useMoment || '');
    setRegion(inputs.region || 'Bangladesh');
    runAnalysis(inputs);
  };

  const canAnalyze = ideaDescription.trim().length > 0;

  return (
    <InstrumentLayout
      title="Competitor Analyzer"
      description="Find brands and products solving a similar problem — with explicit reasons why they overlap."
      icon={Target}
    >
      <div className="space-y-6 w-full min-w-0">
        {/* Input Panel */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-4 w-full min-w-0 overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium text-foreground">Analysis Inputs</span>
            <RunHistoryDropdown
              runs={runs}
              loading={runsLoading}
              onLoadRun={handleLoadRun}
              onRerun={handleRerun}
              rerunning={analyzing}
            />
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {/* Idea description */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="ideaDescription" className="text-sm">
                Idea description
              </Label>
              <Textarea
                id="ideaDescription"
                value={ideaDescription}
                onChange={(e) => setIdeaDescription(e.target.value)}
                placeholder="Describe what your idea does and the problem it solves..."
                className="min-h-[80px] resize-none w-full"
              />
            </div>

            {/* Target customer */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="targetCustomer" className="text-sm">
                Target customer / persona
              </Label>
              <Input
                id="targetCustomer"
                value={targetCustomer}
                onChange={(e) => setTargetCustomer(e.target.value)}
                placeholder="e.g., Solo founders, knowledge workers"
                className="w-full"
              />
            </div>

            {/* Use moment */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="useMoment" className="text-sm">
                Use moment / scenario
              </Label>
              <Input
                id="useMoment"
                value={useMoment}
                onChange={(e) => setUseMoment(e.target.value)}
                placeholder="e.g., When they have an idea but no time to develop it"
                className="w-full"
              />
            </div>

            {/* Region */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="region" className="text-sm">
                Region
              </Label>
              <Input
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g., Bangladesh"
                className="w-full"
              />
            </div>
          </div>

          <Button 
            onClick={handleAnalyze} 
            disabled={!canAnalyze || analyzing}
            className="gap-2"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Analyze Competitors
              </>
            )}
          </Button>
        </div>

        {/* Results Area with scroll support */}
        <div className="w-full min-w-0 overflow-hidden">
          {analyzing && <InstrumentLoadingState cardCount={3} />}
          
          {error && !analyzing && (
            <InstrumentErrorState message={error} onRetry={handleRetry} />
          )}
          
          {!result && !analyzing && !error && (
            <NoResultsYetState instrumentType="analysis" />
          )}
          
          {result && !analyzing && !error && (
            <div className="space-y-6 w-full min-w-0 overflow-auto max-h-[60vh]">
              {/* Insight Summary */}
              <InsightSummary insight={result.insight} />

              {/* Competitor Groups */}
              <div className="space-y-6">
                {competitorGroups.map((group) => (
                  <CompetitorGroupSection key={group.approach} group={group} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </InstrumentLayout>
  );
}
